import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import type { JobDetail } from '@/types';

/**
 * Active Job Store — R228 起「每 project **多個** job 並存」。
 *
 * 為什麼需要這個 store:
 * - Backend 沒有 `GET /projects/{pid}/jobs` list endpoint;reload 後沒地方查「哪些 job
 *   還在跑」,必須前端記住 jobId(docs/54 已登記為 P2 建議)
 * - Firestore listener(`useJobListener`)cold-start fetch + onSnapshot 推送都寫進這個 store
 * - 節點鎖(`laneLocks.deriveLaneLocks`)與生成進度清單(`JobsQueuePanel`)都由它派生
 *
 * **R228 — 從「每 project 1 個」改成「每 project 多個」**:
 * Bryan 2026-08-12 拍板「節點鎖住之後,可以調派其他節點進行生成」+「進度改成多筆呈現」。
 * BE 端本來就沒有單 job 限制(`submit_job` 每次各自凍結圖快照 + 各自 reserve 錢包),
 * 「一個 project 同時最多一個 job」從頭到尾是這個 store 自己的限制 → 本輪解除。
 *
 * shape:`activeJobs[projectId][jobId] = JobDetail`;`lastTouched` 同構(per-job TTL)。
 *
 * **minimize API 整組退場**(R228):它存在的理由是「job 進行中 inspector 被全佔,使用者
 * 需要一個逃生口」;現在面板本身隨時可關(`inspectorPanelStore` + 畫布上的
 * 生成中卡片上的指示點是常駐入口,R229),不需要第二套隱藏狀態。終態單筆改走 `clearJob`。
 *
 * 持久化策略:
 * - localStorage(不是 sessionStorage)— 為了 reload 中段不丟 active job
 * - 跨 tab 共享(同 user 開兩個 tab 看同一 project,兩邊都看得到)
 * - onAuthLost 清空(services/index.ts 注入;user 切換 / 登出時不留 stale)
 * - key `hai.activeJob` 已登記在 `clientStorage.ts` 鑰匙總表(R226 機械閘)
 * - 30min TTL:`lastTouched` 逐 job 記時間戳,module load + 任何 set 路徑跑 `pruneStale`
 *
 * @see useJobListener.ts — 每個 active job 一組 Firestore 訂閱(JobListenerBridge)
 * @see features/canvas/laneLocks.ts — 節點鎖派生(取代舊的全畫布 lock)
 */

/** R228 — persist schema version;改動 ActiveJobState shape 時 bump + 寫 migration。 */
export const ACTIVE_JOB_PERSIST_VERSION = 2;

/** R41.2 — 每個 job 的 TTL(ms);超過 `lastTouched + TTL` 視為 stale。 */
export const ACTIVE_JOB_TTL_MS = 30 * 60 * 1000; // 30 min

export interface ActiveJobState {
  /** projectId → jobId → detail。 */
  activeJobs: Record<string, Record<string, JobDetail>>;
  /** projectId → jobId → lastTouched(ms),用於 TTL prune。 */
  lastTouched: Record<string, Record<string, number>>;
  /** 設定 / 更新一筆 job(listener 推送 / cold-start fetch / submit 樂觀寫入都呼叫)。 */
  setActive: (projectId: string, detail: JobDetail) => void;
  /** 清掉某一筆 job(終態後使用者按「清除」;project 內清空即連 project key 一起移除)。 */
  clearJob: (projectId: string, jobId: string) => void;
  /** 清掉所有 — onAuthLost 觸發。 */
  clearAll: () => void;
  /**
   * 丟超過 TTL(`ACTIVE_JOB_TTL_MS`,30min)沒動的 job 條目。
   *
   * `now` 參數可注入給 test 驗;預設 `Date.now()`。Module load 時自動跑一次
   * (防 long-lived tab + 一週前殘留 entry)。
   */
  pruneStale: (now?: number) => void;
}

/**
 * R41.2 — 對 stale partial JobDetail shape normalize(R37 之前 listener 直接 cast
 * snake_case 進 store 留下的 partial shape 不完整)。R228 起只有 migration 用得到。
 *
 * - identity 缺(`job.jobId` 沒)→ return `null` 表示 drop entry
 * - array / record field 補 `?? []` / `?? {}` 給 component-side `.length` access 不 crash
 */
function normalizePartialJobDetail(detail: JobDetail | undefined): JobDetail | null {
  if (!detail?.job?.jobId) return null;
  return {
    ...detail,
    steps: detail.steps ?? [],
    assets: detail.assets ?? [],
    job: {
      ...detail.job,
      failedStepIds: detail.job.failedStepIds ?? [],
      currentStepIds: detail.job.currentStepIds ?? [],
      outputNodeAssets: detail.job.outputNodeAssets ?? {},
    },
  };
}

export const useActiveJobStore = create<ActiveJobState>()(
  persist(
    (set) => ({
      activeJobs: {},
      lastTouched: {},
      setActive: (projectId, detail) =>
        set((state) => {
          const jobId = detail?.job?.jobId;
          // identity 缺 → 無法歸位(也無法之後 clearJob)。丟掉比寫進一個查不到的 key 好。
          if (!jobId) return state;
          return {
            activeJobs: {
              ...state.activeJobs,
              [projectId]: { ...state.activeJobs[projectId], [jobId]: detail },
            },
            lastTouched: {
              ...state.lastTouched,
              [projectId]: { ...state.lastTouched[projectId], [jobId]: Date.now() },
            },
          };
        }),
      clearJob: (projectId, jobId) =>
        set((state) => {
          if (!state.activeJobs[projectId]?.[jobId]) return state;
          const nextJobs = { ...state.activeJobs[projectId] };
          delete nextJobs[jobId];
          const nextTouched = { ...state.lastTouched[projectId] };
          delete nextTouched[jobId];
          const activeJobs = { ...state.activeJobs };
          const lastTouched = { ...state.lastTouched };
          // project 底下清空就連 project key 一起移除(避免長期累積空殼)。
          if (Object.keys(nextJobs).length === 0) {
            delete activeJobs[projectId];
            delete lastTouched[projectId];
          } else {
            activeJobs[projectId] = nextJobs;
            lastTouched[projectId] = nextTouched;
          }
          return { activeJobs, lastTouched };
        }),
      clearAll: () => set({ activeJobs: {}, lastTouched: {} }),
      pruneStale: (now = Date.now()) =>
        set((state) => {
          const activeJobs: ActiveJobState['activeJobs'] = {};
          const lastTouched: ActiveJobState['lastTouched'] = {};
          let pruned = false;
          for (const [pid, jobs] of Object.entries(state.activeJobs)) {
            const keptJobs: Record<string, JobDetail> = {};
            const keptTouched: Record<string, number> = {};
            for (const [jid, detail] of Object.entries(jobs)) {
              const ts = state.lastTouched[pid]?.[jid] ?? 0;
              if (now - ts < ACTIVE_JOB_TTL_MS) {
                keptJobs[jid] = detail;
                keptTouched[jid] = ts;
              } else {
                pruned = true;
              }
            }
            if (Object.keys(keptJobs).length > 0) {
              activeJobs[pid] = keptJobs;
              lastTouched[pid] = keptTouched;
            }
          }
          // 沒 prune 任何 → 回原 state 避免 unnecessary re-render。
          if (!pruned) return state;
          return { activeJobs, lastTouched };
        }),
    }),
    {
      name: 'hai.activeJob',
      version: ACTIVE_JOB_PERSIST_VERSION,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) =>
        ({
          activeJobs: state.activeJobs,
          lastTouched: state.lastTouched,
        }) as Partial<ActiveJobState>,
      /**
       * R228 — migration to version 2(多 job shape)。
       *
       * v0(R37–R41.1)/ v1(R41.2)都是「每 project 單一 JobDetail」:
       *   `activeJobs: Record<pid, JobDetail>` + `lastTouchedByProject: Record<pid, number>`
       * → 包成 `Record<pid, Record<jid, JobDetail>>`。順手跑 v1 的 partial normalize
       * (identity 缺直接 drop),沒有時間戳的補 `now`(TTL 從本次 migration 起算)。
       */
      migrate: (persistedState: unknown, version: number) => {
        const state = persistedState as Partial<ActiveJobState> | undefined;
        if (!state) return { activeJobs: {}, lastTouched: {} } as Partial<ActiveJobState>;
        if (version < 2) {
          const legacy = persistedState as {
            activeJobs?: Record<string, JobDetail>;
            lastTouchedByProject?: Record<string, number>;
          };
          const activeJobs: ActiveJobState['activeJobs'] = {};
          const lastTouched: ActiveJobState['lastTouched'] = {};
          const now = Date.now();
          for (const [pid, rawDetail] of Object.entries(legacy.activeJobs ?? {})) {
            const normalized = normalizePartialJobDetail(rawDetail as JobDetail);
            if (!normalized) continue;
            const jid = normalized.job.jobId;
            activeJobs[pid] = { [jid]: normalized };
            lastTouched[pid] = { [jid]: legacy.lastTouchedByProject?.[pid] ?? now };
          }
          return { activeJobs, lastTouched } as Partial<ActiveJobState>;
        }
        return state;
      },
    },
  ),
);

// R41.2 — module load 時跑一次 prune 清掉超 TTL 的 entry(long-lived tab + 一週前殘留)。
// 注意:這裡跑在 zustand persist rehydrate 之後(同步 module load 完);
// `createJSONStorage(localStorage)` 是同步,OK。
useActiveJobStore.getState().pruneStale();

/** 穩定空物件 — selector 回它避免每次 render 新 ref 觸發無謂 re-render。 */
const EMPTY_JOBS: Readonly<Record<string, JobDetail>> = Object.freeze({});

/**
 * 某 project 的全部 job(jobId → detail;含終態未清除的)。
 *
 * zustand 比參考值:同一份 record 物件不變就不 re-render;`setActive` 一律建新物件。
 */
export function useProjectJobs(projectId: string): Readonly<Record<string, JobDetail>> {
  return useActiveJobStore((s) => s.activeJobs[projectId] ?? EMPTY_JOBS);
}

/** 這個 project 有沒有任何 job(ToolInspector 的 tab strip 用;boolean → 不因 ref 變動重繪)。 */
export function useHasProjectJobs(projectId: string): boolean {
  return useActiveJobStore((s) => Object.keys(s.activeJobs[projectId] ?? {}).length > 0);
}
